<!-- eslint-disable -->
<script setup>
/* eslint-disable */
import { computed, ref, watch } from 'vue';

const props = defineProps({
  preview: { type: Boolean, default: false },
  records: { type: Array, default: () => [] },
  metrics: { type: Array, default: () => [] },
  history: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
});
const emit = defineEmits(['navigate']);

const firstValue = (value, paths, fallback = '') => {
  const found = paths
    .map(path => path.split('.').reduce((item, key) => item?.[key], value))
    .find(item => item !== undefined && item !== null && item !== '');

  return found ?? fallback;
};

const sourceOf = record => record?.source || record || {};
const initialsFor = value =>
  String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const formatDate = value => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(
    parsed
  );
};

const titleFor = value =>
  String(value || 'Prazo não disponibilizado')
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

const addressFor = source => {
  const address = source?.transaction?.address || {};
  const line = [address.endereco || address.logradouro, address.numero]
    .filter(Boolean)
    .join(', ');
  const locality = [address.bairro, address.cidade, address.uf]
    .filter(Boolean)
    .join(' · ');

  return (
    [line, address.unidade, address.complemento, locality]
      .filter(Boolean)
      .join(' · ') || 'Endereço não disponibilizado'
  );
};

const deadlineTone = bucket => {
  if (bucket === 'vermelhos') return 'bg-rose-50 text-rose-600';
  if (bucket === 'laranjas' || bucket === 'amarelos') {
    return 'bg-amber-50 text-amber-700';
  }
  if (bucket === 'em_dia') return 'bg-emerald-50 text-emerald-700';
  return 'bg-[#f2eff8] text-[#66739b]';
};

const deadlineBorder = bucket => {
  if (bucket === 'vermelhos') return 'border-rose-200 bg-rose-50 text-rose-600';
  if (bucket === 'laranjas' || bucket === 'amarelos') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  if (bucket === 'em_dia') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  return 'border-[#e0dbea] bg-[#faf9fc] text-[#8a94ab]';
};

const deadlineIcon = deadline => {
  const code = String(deadline.code || '').toLocaleLowerCase('pt-BR');
  if (code.includes('escrit')) return 'i-lucide-pen-line';
  if (code.includes('registro')) return 'i-lucide-file-check-2';
  if (code.includes('vistoria')) return 'i-lucide-search-check';
  if (code.includes('chave')) return 'i-lucide-key-round';
  if (code.includes('itbi')) return 'i-lucide-landmark';
  if (code.includes('document')) return 'i-lucide-file-text';
  return 'i-lucide-list-checks';
};

const stagesFor = source => {
  const deadlines = Array.isArray(source?.deadlines) ? source.deadlines : [];
  const stages = deadlines.slice(0, 5).map((deadline, index) => ({
    bucket: deadline.bucket || 'sem_prazo',
    code: deadline.code || `deadline-${index}`,
    dueAt: deadline.due_at,
    icon: deadlineIcon(deadline),
    label: titleFor(deadline.message?.status || deadline.code),
  }));

  if (stages.length) return stages;

  const status = firstValue(source, ['status_label', 'status'], '');
  return status
    ? [
        {
          bucket: source?.deadline?.bucket || 'sem_prazo',
          code: `status-${source.status || 'current'}`,
          dueAt: source.status_due_at,
          icon: 'i-lucide-route',
          label: status,
        },
      ]
    : [];
};

const rows = computed(() =>
  props.records.map(record => {
    const source = sourceOf(record);
    const deadline = source.deadline || {};
    const stages = stagesFor(source);

    return {
      address: addressFor(source),
      code: firstValue(
        source,
        ['transaction.reference', 'reference', 'id'],
        'Código não disponibilizado'
      ),
      dueAt: firstValue(source, ['status_due_at', 'deadline.due_at'], ''),
      dueBucket: deadline.bucket || stages[0]?.bucket || 'sem_prazo',
      dueLabel: deadline.label || 'Sem prazo',
      id: String(record.id ?? source.id ?? ''),
      manager: firstValue(source, ['manager.name'], ''),
      priority: firstValue(source, ['priority'], 'normal'),
      responsible: firstValue(source, ['responsible.name'], ''),
      stages,
      status:
        record.status ||
        firstValue(source, ['status_label', 'status'], 'Sem status'),
      title:
        record.title ||
        firstValue(
          source,
          ['transaction.title', 'transaction.address.title', 'id'],
          'Processo não disponibilizado'
        ),
      updatedAt: firstValue(
        source,
        ['updated_at', 'table.status_changed_at'],
        ''
      ),
    };
  })
);

const selectedId = ref('');
watch(
  rows,
  list => {
    if (!list.some(row => row.id === selectedId.value)) {
      selectedId.value = list[0]?.id || '';
    }
  },
  { immediate: true }
);

const selected = computed(
  () => rows.value.find(row => row.id === selectedId.value) || null
);
const metricCards = computed(() => {
  const returnedMetrics = props.metrics.slice(0, 4);
  const missingCount = Math.max(0, 4 - returnedMetrics.length);

  return [
    ...returnedMetrics,
    ...Array.from({ length: missingCount }, (_, index) => ({
      icon: 'i-lucide-chart-no-axes-column',
      label: `KPI ${returnedMetrics.length + index + 1}`,
      value: '—',
    })),
  ];
});
const milestoneSlots = computed(() => {
  const stages = selected.value?.stages || [];
  const missingCount = Math.max(0, 5 - stages.length);

  return [
    ...stages,
    ...Array.from({ length: missingCount }, (_, index) => ({
      bucket: 'sem_prazo',
      code: `unavailable-stage-${index}`,
      icon: 'i-lucide-circle-dashed',
      label: 'Etapa não disponibilizada',
      unavailable: true,
    })),
  ];
});
const priorityRows = computed(() =>
  rows.value
    .filter(row =>
      ['vermelhos', 'laranjas', 'amarelos'].includes(row.dueBucket)
    )
    .slice(0, 3)
);
const highlightedRows = computed(() => rows.value.slice(0, 3));
const activityRows = computed(() =>
  props.history.slice(0, 5).map(item => ({
    actor: item.actor || item.author || 'Sistema',
    date: item.date || item.created_at || '',
    id: String(item.id || item.sequence || item.label || item.action || ''),
    label: item.label || item.action || 'Atualização registrada',
  }))
);
const activeCount = computed(() => rows.value.length);
const canOpenSelected = computed(() => Boolean(selected.value?.id));

const selectRow = id => {
  selectedId.value = id;
};

const openSelected = () => {
  if (!canOpenSelected.value) return;

  emit('navigate', { id: selected.value.id, screen: 'detail' });
};

const editSelected = () => {
  if (!canOpenSelected.value) return;

  emit('navigate', { id: selected.value.id, screen: 'edit' });
};
</script>

<template>
  <main class="mx-auto w-full max-w-none pb-5 pl-3 pr-4 pt-7 text-[#101958]">
    <section class="grid grid-cols-[minmax(0,1fr)_416px] items-start gap-10">
      <section class="min-w-0">
        <article data-imudei-visual-id="post-sale-dashboard-header" class="h-[270px] rounded-2xl border border-[#eeeaf4] bg-white p-6 shadow-[0_7px_22px_rgba(38,25,86,0.025)]">
          <header class="flex items-start justify-between gap-4">
            <span class="min-w-0">
              <span data-imudei-visual-id="post-sale-dashboard-table" class="flex items-center gap-2 text-[9px] font-medium text-[#7480a0]">
                <span>Pós-venda</span>
                <i class="size-1 rounded-full bg-[#aeb7cb]" />
                <button
                  v-for="row in rows.slice(0, 3)"
                  :key="row.id || row.code"
                  class="truncate text-left text-[#66739b]"
                  :class="selected?.id === row.id ? 'font-semibold text-[#5a24c7]' : ''"
                  type="button"
                  @click="selectRow(row.id)"
                >
                  {{ row.code }}
                </button>
                <span v-if="!rows.length">Nenhum processo retornado</span>
              </span>
              <span class="mt-2 flex items-center gap-3">
                <h1 class="truncate text-[20px] font-bold tracking-[-0.025em]">
                  {{ selected?.title || 'Nenhum processo em pós-venda' }}
                </h1>
                <small
                  v-if="selected"
                  class="shrink-0 rounded-full px-2.5 py-1 text-[8px] font-semibold"
                  :class="deadlineTone(selected.dueBucket)"
                >
                  {{ selected.status }}
                </small>
              </span>
            </span>

            <div data-imudei-visual-id="post-sale-dashboard-filters" class="flex shrink-0 items-center gap-2">
              <button
                class="flex h-9 items-center gap-2 rounded-xl border border-[#e5dfed] bg-white px-4 text-[8px] font-semibold text-[#5a24c7] disabled:cursor-not-allowed disabled:border-[#ece9f1] disabled:text-[#a0a8bb]"
                :disabled="!canOpenSelected"
                type="button"
                @click="editSelected"
              >
                <i class="i-lucide-pencil size-4" />Editar processo
              </button>
              <button
                class="flex h-9 items-center gap-2 rounded-xl bg-[#ded8ea] px-4 text-[8px] font-bold text-[#858da3]"
                disabled
                type="button"
                title="A API atual não disponibiliza criação de tarefas de pós-venda"
              >
                <i class="i-lucide-plus size-4" />Nova tarefa
              </button>
              <button
                class="grid size-9 place-items-center rounded-xl border border-[#e8e4ef] text-[#5a24c7] disabled:cursor-not-allowed disabled:text-[#a0a8bb]"
                :disabled="!canOpenSelected"
                title="Abrir processo"
                type="button"
                @click="openSelected"
              >
                <i class="i-lucide-ellipsis-vertical size-4" />
              </button>
            </div>
          </header>

          <section v-if="selected" data-imudei-visual-id="post-sale-dashboard-detail" class="mt-3 grid grid-cols-[238px_minmax(0,1fr)] gap-6">
            <div class="grid h-[150px] place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-[#203a64] via-[#516fa9] to-[#f3a458] text-white shadow-[0_8px_20px_rgba(31,50,86,0.14)]">
              <i class="i-lucide-building-2 size-16 opacity-90" />
            </div>

            <div data-imudei-visual-id="post-sale-dashboard-metrics" class="flex h-[150px] min-w-0 flex-col justify-between">
              <div class="grid grid-cols-[minmax(0,1.15fr)_minmax(160px,.85fr)] gap-6">
                <div class="space-y-2 text-[10px]">
                  <span class="flex items-center gap-2 font-semibold"><i class="i-lucide-building-2 size-4 text-[#6276b0]" />{{ selected.code }}</span>
                  <span class="flex items-center gap-2 text-[#364678]"><i class="i-lucide-map-pin size-4 text-[#6276b0]" />{{ selected.address }}</span>
                  <span class="flex items-center gap-2 text-[#59698f]"><i class="i-lucide-route size-4 text-[#6276b0]" />{{ selected.status }}</span>
                </div>
                <div class="grid grid-cols-2 gap-4 border-l border-[#eeeaf4] pl-5">
                  <span>
                    <small class="block text-[8px] text-[#7480a0]">Próximo prazo</small>
                    <strong class="mt-1 block text-[10px]">{{ formatDate(selected.dueAt) }}</strong>
                    <small class="mt-2 inline-block rounded-full px-2 py-1 text-[7px]" :class="deadlineTone(selected.dueBucket)">{{ selected.dueLabel }}</small>
                  </span>
                  <span>
                    <small class="block text-[8px] text-[#7480a0]">Atualizado</small>
                    <strong class="mt-1 block text-[10px]">{{ formatDate(selected.updatedAt) }}</strong>
                  </span>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-6">
                <span>
                  <small class="block text-[8px] text-[#59698f]">Responsável</small>
                  <span class="mt-2 flex items-center gap-2">
                    <i class="grid size-9 place-items-center rounded-full border-2 border-violet-500 bg-violet-100 text-[8px] font-bold text-[#5a24c7]">{{ initialsFor(selected.responsible) || '—' }}</i>
                    <strong class="truncate text-[9px]">{{ selected.responsible || 'Não disponibilizado' }}</strong>
                  </span>
                </span>
                <span>
                  <small class="block text-[8px] text-[#59698f]">Gestor do processo</small>
                  <span class="mt-2 flex items-center gap-2">
                    <i class="grid size-9 place-items-center rounded-full border-2 border-[#5a24c7] bg-[#eee6ff] text-[8px] font-bold text-[#5a24c7]">{{ initialsFor(selected.manager) || '—' }}</i>
                    <strong class="truncate text-[9px]">{{ selected.manager || 'Não disponibilizado' }}</strong>
                  </span>
                </span>
              </div>
            </div>
          </section>

          <section v-else data-imudei-visual-id="post-sale-dashboard-detail" class="mt-3 grid h-[150px] place-items-center rounded-xl border border-dashed border-[#e2ddeb] bg-[#fcfbff] text-center text-[#8b95ad]">
            <span data-imudei-visual-id="post-sale-dashboard-metrics">
              <i class="i-lucide-building-2 mx-auto size-8" />
              <strong class="mt-3 block text-[10px]">Aguardando um processo</strong>
              <small class="mt-1 block text-[8px]">A API ainda não retornou processos de pós-venda.</small>
            </span>
          </section>
        </article>

        <article data-imudei-visual-id="post-sale-dashboard-tabs" class="mt-3 h-[210px] overflow-hidden rounded-2xl border border-[#eeeaf4] bg-white px-5 py-4 shadow-[0_5px_18px_rgba(38,25,86,0.02)]">
          <header class="flex items-center justify-between">
            <span>
              <h2 class="text-[11px] font-bold">Temporada de marcos</h2>
              <p class="mt-1 text-[7px] text-[#7480a0]">Prazos e etapas retornados para o processo selecionado.</p>
            </span>
            <small class="text-[8px] font-semibold text-[#5a24c7]">{{ selected?.code || 'Sem processo' }}</small>
          </header>

          <div class="relative mt-4 grid grid-cols-5">
            <i class="absolute left-[9%] right-[9%] top-7 h-px border-t border-dashed border-[#cfc6ea]" />
            <span v-for="(stage, index) in milestoneSlots" :key="stage.code" class="relative z-10 flex min-w-0 flex-col items-center text-center">
              <i class="grid size-12 place-items-center rounded-2xl border-2 bg-white text-[16px] font-bold shadow-[0_5px_12px_rgba(53,30,112,0.08)]" :class="stage.unavailable ? 'border-[#d9d4e6] text-[#9ca6bd]' : deadlineBorder(stage.bucket)">
                <i :class="stage.icon" class="size-5" />
              </i>
              <strong class="mt-3 max-w-[132px] truncate text-[8px]">{{ stage.label }}</strong>
              <small class="mt-1 text-[7px]" :class="stage.unavailable ? 'text-[#8b95ad]' : deadlineTone(stage.bucket)">{{ stage.unavailable ? 'Indisponível' : formatDate(stage.dueAt) }}</small>
              <small v-if="!stage.unavailable" class="mt-2 rounded-full bg-violet-50 px-2 py-1 text-[7px] font-semibold text-[#5a24c7]">Etapa {{ index + 1 }}</small>
            </span>
          </div>
        </article>

        <section class="mt-3 grid grid-cols-[minmax(0,.72fr)_minmax(0,1fr)] gap-4">
          <article class="h-[238px] rounded-2xl border border-[#eeeaf4] bg-white p-4 shadow-[0_4px_15px_rgba(38,25,86,0.018)]">
            <header class="flex items-center justify-between">
              <h2 class="text-[10px] font-bold">Pendências prioritárias</h2>
              <small class="text-[7px] text-[#5a24c7]">{{ priorityRows.length }} em atenção</small>
            </header>
            <div v-if="priorityRows.length" class="mt-4 space-y-3">
              <button v-for="row in priorityRows" :key="row.id" class="flex w-full items-center gap-3 text-left" type="button" @click="selectRow(row.id)">
                <i class="grid size-7 place-items-center rounded-lg" :class="deadlineTone(row.dueBucket)"><i class="i-lucide-calendar-days size-4" /></i>
                <span class="min-w-0 flex-1">
                  <strong class="block truncate text-[8px]">{{ row.title }}</strong>
                  <small class="mt-1 block truncate text-[7px] text-[#7480a0]">{{ row.responsible || 'Responsável não disponibilizado' }}</small>
                </span>
                <small class="rounded-full px-2 py-1 text-[7px]" :class="deadlineTone(row.dueBucket)">{{ formatDate(row.dueAt) }}</small>
              </button>
            </div>
            <div v-else class="grid h-[132px] place-items-center text-center text-[#8b95ad]">
              <span><i class="i-lucide-circle-check-big mx-auto size-5" /><small class="mt-2 block text-[8px]">Nenhum prazo em atenção foi retornado.</small></span>
            </div>
          </article>

          <article class="h-[238px] rounded-2xl border border-[#eeeaf4] bg-white p-4 shadow-[0_4px_15px_rgba(38,25,86,0.018)]">
            <header class="flex items-center justify-between">
              <h2 class="text-[10px] font-bold">Documentos do processo</h2>
              <small class="text-[7px] text-[#5a24c7]">Sem contrato</small>
            </header>
            <div class="mt-4 grid grid-cols-2 gap-2">
              <span v-for="slot in 4" :key="slot" class="flex h-[58px] items-center gap-3 rounded-xl border border-dashed border-[#e2ddeb] bg-[#fcfbff] px-3 text-[#8b95ad]">
                <i class="i-lucide-file-text size-4" />
                <small class="text-[7px]">Documento não disponibilizado</small>
              </span>
            </div>
          </article>
        </section>

        <article data-imudei-visual-id="post-sale-dashboard-footer" class="mt-3 h-[190px] overflow-hidden rounded-2xl border border-[#eeeaf4] bg-white px-5 py-4 shadow-[0_4px_15px_rgba(38,25,86,0.018)]">
          <header class="flex items-center justify-between">
            <span>
              <h2 class="text-[10px] font-bold">Conquistas por fase</h2>
              <p class="mt-1 text-[7px] text-[#7480a0]">Pontos e recompensas não são retornados pela API atual.</p>
            </span>
            <small class="text-[7px] text-[#7480a0]">Indisponível</small>
          </header>
          <div class="mt-4 grid grid-cols-5 gap-3 text-center">
            <span v-for="stage in milestoneSlots" :key="`reward-${stage.code}`" class="min-w-0">
              <i class="mx-auto grid size-11 place-items-center rounded-2xl border-2" :class="stage.unavailable ? 'border-[#d9d4e6] bg-[#faf9fd] text-[#9ca6bd]' : deadlineBorder(stage.bucket)"><i :class="stage.icon" class="size-5" /></i>
              <strong class="mt-2 block truncate text-[7px]">{{ stage.label }}</strong>
              <small class="mt-1 block text-[7px]" :class="stage.unavailable ? 'text-[#8b95ad]' : 'text-[#5a24c7]'">{{ stage.unavailable ? 'Sem dados' : 'Em acompanhamento' }}</small>
            </span>
          </div>
        </article>
      </section>

      <aside class="mt-[74px] space-y-3">
        <article class="h-[120px] rounded-2xl border border-[#eeeaf4] bg-white p-4 shadow-[0_4px_15px_rgba(38,25,86,0.018)]">
          <header class="flex items-center justify-between">
            <h2 class="text-[10px] font-bold">Missão diária</h2>
            <small class="text-[7px] text-[#7480a0]">Indisponível</small>
          </header>
          <div class="mt-4 flex items-center gap-3">
            <i class="grid size-8 place-items-center rounded-full bg-violet-50 text-[#5a24c7]"><i class="i-lucide-target size-4" /></i>
            <span class="min-w-0 flex-1">
              <strong class="block text-[8px]">Missões não disponibilizadas</strong>
              <small class="mt-1 block text-[7px] text-[#7480a0]">A API não retorna pontos ou recompensas de pós-venda.</small>
            </span>
          </div>
          <i class="mt-3 block h-1.5 rounded-full bg-violet-100"><i class="block h-full w-0 rounded-full bg-[#5a24c7]" /></i>
        </article>

        <article class="h-[473px] overflow-hidden rounded-2xl border border-[#eeeaf4] bg-white p-4 shadow-[0_4px_15px_rgba(38,25,86,0.018)]">
          <header class="flex items-center justify-between">
            <h2 class="text-[10px] font-bold">Timeline social</h2>
            <small class="rounded-lg border border-[#e8e4ef] px-3 py-1.5 text-[7px] text-[#7480a0]">Histórico</small>
          </header>
          <div v-if="activityRows.length" class="mt-4 divide-y divide-[#f0edf5]">
            <div v-for="item in activityRows" :key="item.id" class="flex min-h-[70px] items-center gap-3 py-3">
              <i class="grid size-8 shrink-0 place-items-center rounded-full bg-[#eee6ff] text-[8px] font-bold text-[#5a24c7]">{{ initialsFor(item.actor) || '—' }}</i>
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[8px]">{{ item.actor }}</strong>
                <small class="mt-1 block line-clamp-2 text-[7px] leading-3 text-[#7480a0]">{{ item.label }}</small>
              </span>
              <small class="self-start pt-1 text-[7px] text-[#7480a0]">{{ formatDate(item.date) }}</small>
            </div>
          </div>
          <div v-else class="grid h-[390px] place-items-center text-center text-[#8b95ad]">
            <span><i class="i-lucide-history mx-auto size-6" /><strong class="mt-3 block text-[9px]">Histórico indisponível</strong><small class="mt-1 block text-[7px]">Abra um processo para carregar eventos retornados pela API.</small></span>
          </div>
        </article>

        <article class="h-[200px] rounded-2xl border border-[#eeeaf4] bg-white p-4 shadow-[0_4px_15px_rgba(38,25,86,0.018)]">
          <header class="flex items-center justify-between">
            <h2 class="text-[10px] font-bold">Ranking da equipe</h2>
            <small class="text-[7px] text-[#7480a0]">Sem contrato</small>
          </header>
          <div class="grid h-[132px] place-items-center text-center text-[#8b95ad]">
            <span><i class="i-lucide-trophy mx-auto size-5" /><strong class="mt-2 block text-[8px]">Ranking não disponibilizado</strong><small class="mt-1 block text-[7px]">A API atual não retorna classificação da equipe.</small></span>
          </div>
        </article>
      </aside>
    </section>
  </main>
</template>
